// Transaction fee calculation utilities for HR ERP system

/**
 * Default transaction fee tiers for USD payouts based on PKR salary ranges
 */
export const DEFAULT_TRANSACTION_FEE_TIERS = [
  { minPkr: 100000, maxPkr: 150000, usdFee: 10 },
  { minPkr: 150001, maxPkr: 200000, usdFee: 15 },
  { minPkr: 200001, maxPkr: 250000, usdFee: 20 },
  { minPkr: 250001, maxPkr: 300000, usdFee: 25 },
  { minPkr: 300001, maxPkr: 350000, usdFee: 30 },
  { minPkr: 350001, maxPkr: Infinity, usdFee: 30 }
];

/**
 * Calculate transaction fee for USD payout based on PKR salary amount
 * @param {number} pkrAmount - PKR salary amount
 * @param {Array} feeTiers - Array of fee tier objects (optional, uses default if not provided)
 * @returns {number} USD transaction fee amount
 */
export function calculateTransactionFee(pkrAmount, feeTiers = DEFAULT_TRANSACTION_FEE_TIERS) {
  if (!pkrAmount || pkrAmount <= 0) {
    return 0;
  }

  // Find the appropriate fee tier
  const tier = feeTiers.find(tier => 
    pkrAmount >= tier.minPkr && pkrAmount <= tier.maxPkr
  );

  return tier ? tier.usdFee : 0;
}

/**
 * Calculate net USD amount after deducting transaction fee
 * @param {number} grossUsdAmount - Gross USD amount before fee
 * @param {number} pkrAmount - PKR salary amount (for fee calculation)
 * @param {Array} feeTiers - Array of fee tier objects (optional)
 * @returns {Object} Object containing net USD amount and fee details
 */
export function calculateNetUsdAmount(grossUsdAmount, pkrAmount, feeTiers = DEFAULT_TRANSACTION_FEE_TIERS) {
  if (!grossUsdAmount || grossUsdAmount <= 0) {
    return {
      grossUsdAmount: 0,
      transactionFee: 0,
      netUsdAmount: 0
    };
  }

  const transactionFee = calculateTransactionFee(pkrAmount, feeTiers);
  const netUsdAmount = Math.max(0, grossUsdAmount - transactionFee);

  return {
    grossUsdAmount,
    transactionFee,
    netUsdAmount
  };
}

/**
 * Get fee tier description for a given PKR amount
 * @param {number} pkrAmount - PKR salary amount
 * @param {Array} feeTiers - Array of fee tier objects (optional)
 * @returns {string} Human-readable fee tier description
 */
export function getFeeTierDescription(pkrAmount, feeTiers = DEFAULT_TRANSACTION_FEE_TIERS) {
  if (!pkrAmount || pkrAmount <= 0) {
    return 'No fee applicable';
  }

  const tier = feeTiers.find(tier => 
    pkrAmount >= tier.minPkr && pkrAmount <= tier.maxPkr
  );

  if (!tier) {
    return 'Fee tier not found';
  }

  if (tier.maxPkr === Infinity) {
    return `PKR ${tier.minPkr.toLocaleString()}+: $${tier.usdFee} USD fee`;
  }

  return `PKR ${tier.minPkr.toLocaleString()} - ${tier.maxPkr.toLocaleString()}: $${tier.usdFee} USD fee`;
}

/**
 * Validate fee tier configuration
 * @param {Array} feeTiers - Array of fee tier objects
 * @returns {Object} Validation result with isValid boolean and errors array
 */
export function validateFeeTiers(feeTiers) {
  const errors = [];

  if (!Array.isArray(feeTiers) || feeTiers.length === 0) {
    errors.push('Fee tiers must be a non-empty array');
    return { isValid: false, errors };
  }

  // Check for required properties
  for (let i = 0; i < feeTiers.length; i++) {
    const tier = feeTiers[i];
    if (!tier.hasOwnProperty('minPkr') || !tier.hasOwnProperty('maxPkr') || !tier.hasOwnProperty('usdFee')) {
      errors.push(`Tier ${i + 1}: Missing required properties (minPkr, maxPkr, usdFee)`);
    }
  }

  // Check for valid ranges and overlaps
  for (let i = 0; i < feeTiers.length; i++) {
    const tier = feeTiers[i];
    
    if (tier.minPkr < 0 || tier.maxPkr < 0) {
      errors.push(`Tier ${i + 1}: PKR amounts must be non-negative`);
    }
    
    if (tier.minPkr >= tier.maxPkr && tier.maxPkr !== Infinity) {
      errors.push(`Tier ${i + 1}: minPkr must be less than maxPkr`);
    }
    
    if (tier.usdFee < 0) {
      errors.push(`Tier ${i + 1}: USD fee must be non-negative`);
    }

    // Check for overlaps with previous tiers
    if (i > 0) {
      const prevTier = feeTiers[i - 1];
      if (tier.minPkr <= prevTier.maxPkr && prevTier.maxPkr !== Infinity) {
        errors.push(`Tier ${i + 1}: Overlaps with previous tier`);
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Format transaction fee for display
 * @param {number} fee - Transaction fee amount
 * @param {string} currency - Currency code (default: 'USD')
 * @returns {string} Formatted fee string
 */
export function formatTransactionFee(fee, currency = 'USD') {
  if (fee === 0) {
    return 'No fee';
  }
  
  const currencySymbols = {
    USD: '$',
    PKR: '₨',
    EUR: '€',
    GBP: '£'
  };
  
  const symbol = currencySymbols[currency] || currency;
  return `${symbol}${fee.toFixed(2)}`;
} 